import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useCompleteJob } from '@/hooks/useJobs';
import { useToast } from '@/hooks/use-toast';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { useVisibility } from '@/hooks/useVisibility';
import { netFromGross } from '@/utils/tax';
import { format } from 'date-fns';
import { formatInTz } from '@/utils/timezone';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { Check, Calendar, AlertTriangle } from 'lucide-react';
import type { JobWithRelations } from '@/types';
import type { JobFinancials, JobPayment } from './CompleteJobDialog';

interface CompleteJobSummaryStepProps {
  job: JobWithRelations;
  financials: JobFinancials;
  payments: JobPayment[];
  onComplete: () => void;
  onBack: () => void;
  onCancel: () => void;
}

export function CompleteJobSummaryStep({ job, financials, payments, onComplete, onBack, onCancel }: CompleteJobSummaryStepProps) {
  const completeJob = useCompleteJob();
  const { toast } = useToast();
  const tax = useCompanyTax();
  const { canSeeProfit } = useVisibility();
  const { locale, timezone, formatMoney, formatShortDate } = useCompanyLocale();

  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const revenue = financials.invoice_total;

  const getPaymentStatus = () => {
    if (totalPaid === 0) return { label: 'UNPAID', color: 'bg-destructive' };
    if (totalPaid >= revenue) return { label: 'FULLY PAID', color: 'bg-success' };
    return { label: 'PARTIALLY PAID', color: 'bg-warning' };
  };

  const paymentStatus = getPaymentStatus();
  const revenueBeforeGst = netFromGross(revenue, tax);
  const jobProfitBeforeTech = revenueBeforeGst - financials.total_parts_cost - financials.toll_cost;
  const techCommissionPct = job.techs?.commission_percentage ?? 0;
  const companyProfit = jobProfitBeforeTech * (1 - techCommissionPct / 100);
  // Customer-facing split: the service line absorbs everything the parts (at sell) don't.
  const serviceLine = Math.max(revenue - financials.total_parts_sell, 0);

  const handleComplete = () => {
    completeJob.mutate(
      {
        jobId: job.id,
        financials,
        payments,
        // The wizard never edits the close date (the hook stamps "now" in the company
        // timezone); fall back to now so the summary never shows "Invalid Date".
        closeDate:
          financials.job_date instanceof Date && !isNaN(financials.job_date.getTime())
            ? financials.job_date
            : new Date(),
      },
      {
        onSuccess: () => onComplete(),
        // Without this a failure (e.g. a job scheduled in the future) left the dialog
        // open with no feedback and looked like a dead button.
        onError: (error: Error) => {
          toast({
            title: 'Could not close the job',
            description: error.message,
            variant: 'destructive',
          });
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="font-semibold text-lg">Step 3: Review & Confirm</h3>

        <Card className="p-6 bg-muted/50">
          <h4 className="font-semibold mb-4 flex items-center gap-2">
            <Check className="h-5 w-5 text-success" />
            Financial Summary
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Close Date:
              </span>
              <span className="font-semibold">
                {financials.job_date instanceof Date && !isNaN(financials.job_date.getTime())
                  ? format(financials.job_date, locale.longDate)
                  : format(new Date(), locale.longDate)}
              </span>
            </div>
            <Separator className="my-2" />
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground pt-1">What the customer will see</p>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Service / labour:</span>
              <span className="font-semibold">{formatMoney(serviceLine)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Parts:</span>
              <span className="font-semibold">{formatMoney(financials.total_parts_sell)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Invoice Total:</span>
              <span className="font-semibold">{formatMoney(revenue)}</span>
            </div>
            <Separator className="my-2" />
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground pt-1">Internal</p>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Parts Cost:</span>
              <span className="font-semibold">{formatMoney(financials.total_parts_cost)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Toll Cost:</span>
              <span className="font-semibold">{formatMoney(financials.toll_cost)}</span>
            </div>
            {canSeeProfit && (
              <>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tech Commission ({techCommissionPct}%):</span>
                  <span className="font-semibold">{formatMoney(jobProfitBeforeTech - companyProfit)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold">Company Profit:</span>
                  <span className="font-bold text-lg text-primary">{formatMoney(companyProfit)}</span>
                </div>
              </>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold flex items-center gap-2">
              <Check className="h-5 w-5 text-success" />
              Payment Breakdown
            </h4>
            <Badge className={paymentStatus.color} variant="default">
              {paymentStatus.label}
            </Badge>
          </div>
          <div className="space-y-3">
            {payments.map((payment, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                <div>
                  <p className="font-medium capitalize">{payment.payment_method.replace('_', ' ')}</p>
                  <p className="text-sm text-muted-foreground">
                    {payment.payment_date instanceof Date && !isNaN(payment.payment_date.getTime())
                      ? formatShortDate(payment.payment_date)
                      : typeof payment.payment_date === 'string'
                        ? formatShortDate(payment.payment_date)
                        : 'Invalid Date'}
                  </p>
                  {payment.notes && (
                    <p className="text-sm text-muted-foreground italic">{payment.notes}</p>
                  )}
                </div>
                <span className="font-semibold">{formatMoney(payment.amount)}</span>
              </div>
            ))}
            <Separator />
            <div className="flex justify-between items-center pt-2">
              <span className="font-semibold text-lg">Total Payments:</span>
              <span className="font-bold text-xl text-success">{formatMoney(totalPaid)}</span>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-warning/20 bg-warning/5">
          <div className="flex gap-2 items-start text-warning">
            <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Confirm Posting</p>
              <p className="text-sm mt-1">
                Clicking below will <strong>POST</strong> this revenue to the current month ({formatInTz(new Date(), timezone, 'MMMM yyyy')}).
                This action cannot be undone.
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack} disabled={completeJob.isPending}>
          Back
        </Button>
        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onCancel} disabled={completeJob.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleComplete}
            disabled={completeJob.isPending}
            className="bg-success hover:bg-success/90 text-primary-foreground"
          >
            {completeJob.isPending ? 'Posting...' : 'Close & Post Job'}
          </Button>
        </div>
      </div>
    </div>
  );
}
